import * as React from 'react';
import {
  Stepper as MatStepper,
  Step as MatStep,
  SxProps,
  Theme,
  Box,
  svgIconClasses,
  stepIconClasses,
  stepperClasses,
  stepLabelClasses,
  stepButtonClasses
} from '@mui/material';
import { Typography } from '../Typography';
import { StepButton } from '../StepButton';
import { archerConstants, theme } from '../../utils';

export interface StepperProps {
  sx?: SxProps<Theme>;
  nonLinear?: boolean;
  orientation?: 'horizontal' | 'vertical';
  steps: string[];
  onStepClick: (index: number) => any;
  completed: number[];
  activeStep: number;
  className?: string;
  focusRipple?: boolean;
  disableRipple?: boolean;
}

const isActive = (index: number, activeStep: number) => {
  return index === activeStep;
};

const styles: Record<string, SxProps<Theme>> = {
  stepperWrapper: {
    backgroundColor: 'inherit',
    [`& .${stepperClasses['root']}`]: {
      padding: 1
    },
    [`& .${svgIconClasses['root']}`]: {
      height: theme.spacing(3),
      width: theme.spacing(3)
    },
    [`& .${stepLabelClasses['iconContainer']}`]: {
      [`& .${stepIconClasses['text']}`]: {
        fontSize: '16px !important',
        transform: 'translate(0px, 2px)'
      }
    },
    [`& .${stepButtonClasses['root']}`]: {
      '&:focus, &:active': {
        backgroundColor: 'transparent'
      }
    }
  },
  stepLabelContent: {
    fontSize: theme.spacing(2),
    lineHeight: 1.5
  },
  stepButtonRipple: {
    [`& .${stepButtonClasses['touchRipple']}`]: {
      color: archerConstants.archerButtonActive
    }
  }
};

const Stepper: React.FunctionComponent<StepperProps> = ({
  sx,
  orientation,
  nonLinear,
  steps,
  completed,
  activeStep,
  className,
  onStepClick,
  focusRipple,
  disableRipple = false
}: StepperProps) => {
  const { stepperWrapper, stepLabelContent, stepButtonRipple } = styles;

  function isStepComplete(step: number) {
    return completed.includes(step);
  }

  return (
    <Box sx={stepperWrapper}>
      <MatStepper sx={sx} alternativeLabel className={className} activeStep={activeStep} orientation={orientation} nonLinear={nonLinear}>
        {steps.map((label: string, index: number) => (
          <MatStep key={label} active={isActive(index, activeStep)} completed={isStepComplete(index)} sx={stepButtonRipple}>
            <StepButton focusRipple={focusRipple || false} onClick={() => onStepClick(index)} icon={index + 1} disableRipple={disableRipple}>
              <Typography
                sx={stepLabelContent}
                variant={isActive(index, activeStep) ? 'h4' : 'body1'}
                color={(() => {
                  if (isActive(index, activeStep)) {
                    return 'primary';
                  }
                  if (isStepComplete(index)) {
                    return 'textPrimary';
                  }
                  return 'textSecondary';
                })()}
              >
                {label}
              </Typography>
            </StepButton>
          </MatStep>
        ))}
      </MatStepper>
    </Box>
  );
};

Stepper.defaultProps = {
  completed: [],
  orientation: 'horizontal',
  activeStep: 0
};

Stepper.displayName = 'Stepper';

export { Stepper };
